import { useEffect, useRef, useState } from 'react';
import { toBool, CportalService } from 'vopay';
import { useHistory, useLocation } from 'react-router-dom';
import { ClientAccountDetailData, ContactDetailView } from 'shared/contacts';
import CportalTab from './Cportal/CportalTab';
import LinkDialog from './LinkDialog';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useDialog, useToast } from 'shared';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import useCportal from '@/hooks/useApi/useCportal';
import useAccountingSoftwareHostProps from '@/components/Software/useAccountingSoftwareHostProps';
import useActivityTypeVisibility from '@/hooks/useActivityTypeVisibility';

type ClientAccountDetailProps = {
  accountID: string;
  detail: ClientAccountDetailData;
  // Whether the new flow's embedded Receivables/Payables tabs may show (still
  // gated on the account's accounting locked feature). Off on the menu-linked
  // page, on for the orphan /accounting copy (ID-13335).
  accountingEnabled: boolean;
};

// The client-account detail body — shared/contacts' ContactDetailView mounted
// with the portal's providers mapped onto its host props, plus the
// portal-only cPortal tab wired through extraTabs. The host owns the account
// data via useClientAccountDetailData and renders any chrome itself.
export default function ClientAccountDetail({ accountID, detail, accountingEnabled }: ClientAccountDetailProps) {
  const location = useLocation<{ showLinkDialog?: boolean }>();
  const history = useHistory();
  const { clientAccount, refreshClientAccount } = detail;
  const [tab, setTab] = useState('details');

  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccount' });
  const { t: tCustomer } = useTranslation('translations', { keyPrefix: 'Customers' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const {
    response: { CPortalEnabled, SubAccountPermissions },
  } = useClientData();

  const accounting = useAccountingSoftwareHostProps();
  const { AccountingSoftwareIntegrationEnabled } = useLockedFeatures();
  const activityTypes = useActivityTypeVisibility();
  const { permissions } = usePermissions();
  const toast = useToast();
  const dialog = useDialog();

  const {
    userSettings: { timezone },
  } = useUserSettingsProvider();

  const {
    accountServices,
    clientAccountServices,
    isFetchingClientAccountServices,
    cPortalLink,
    fetchAccountCportalSettings,
    fetchClientAccountServices,
    setClientAccountService,
  } = useCportal();

  const showLinkDialogRef = useRef(location.state?.showLinkDialog ?? false);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const newTab = search.get('cardTab');
    newTab && setTab(newTab);
  }, [location.search]);

  useEffect(() => {
    fetchAccountCportalSettings();
  }, []);

  async function handleLinkDialog(link: string) {
    await dialog.open(({ onCancel }) => <LinkDialog link={link} onClose={onCancel} />);
  }

  useEffect(() => {
    if (cPortalLink && showLinkDialogRef.current) {
      showLinkDialogRef.current = false;
      history.replace(location.pathname + location.search, {});
      handleLinkDialog(cPortalLink);
    }
  }, [cPortalLink]);

  async function handleCportalSettingsUpdate(enabled = false) {
    refreshClientAccount();
    await fetchAccountCportalSettings();
    await fetchClientAccountServices(accountID);
    if (enabled) handleLinkDialog(cPortalLink);
  }

  async function handleToggleService(service: CportalService, enabled: boolean) {
    try {
      await setClientAccountService({
        ClientAccountID: accountID,
        CportalService: service,
        Enabled: enabled,
      });

      await fetchClientAccountServices(accountID);
      toast.success(t('serviceUpdateSuccess', { service, defaultValue: '{{service}} service updated' }));
    } catch (err) {
      toast.neutral(err instanceof Error ? err.message : tGlobal('errorOccurred'));
    }
  }

  const extraTabs =
    CPortalEnabled && clientAccount
      ? [
          {
            value: 'cPortal',
            trigger: tCustomer('cPortal', { defaultValue: 'cPortal' }),
            content: (
              <CportalTab
                clientName={clientAccount.ClientName}
                clientEmail={clientAccount.EmailAddress}
                clientPhone={clientAccount.PhoneNumber}
                clientID={clientAccount.ClientAccountID}
                isAccountActive={clientAccount.IsActive}
                cPortalLink={cPortalLink}
                clientAccountServices={clientAccountServices}
                accountServices={accountServices}
                isFetchingServices={isFetchingClientAccountServices}
                loadServices={() => {
                  fetchClientAccountServices(accountID);
                }}
                onUpdate={handleCportalSettingsUpdate}
                onToggleService={handleToggleService}
              />
            ),
          },
        ]
      : undefined;

  return (
    <ContactDetailView
      clientAccountId={accountID}
      clientAccountsEnabled
      accounting={accounting}
      clientAccountDetail={detail}
      tab={tab}
      onTabChange={setTab}
      accountingEnabled={accountingEnabled && Boolean(AccountingSoftwareIntegrationEnabled)}
      extraTabs={extraTabs}
      activityTypes={activityTypes}
      activityOptions={{
        persistFiltersInUrl: true,
        timezone,
        showIncludeSubAccounts: toBool(SubAccountPermissions) && permissions.ViewSubaccounts,
      }}
    />
  );
}
